import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import api from 'api';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { AppDispatch } from 'redux/store';
import type { ProviderOption } from 'types/servicesDocuments';
import { getErrorMessage } from 'utils/v2/errorHelper';

export function useProviderOptions(enabled = true) {
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const [options, setOptions] = useState<ProviderOption[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!enabled) {
            return;
        }
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const response = await api.companies.getCompanies();
                if (cancelled) {
                    return;
                }
                const entries = Object.entries(response.data || {}).map(([id, name]) => ({
                    id: Number(id),
                    name: String(name),
                }));
                entries.sort((a, b) => a.name.localeCompare(b.name));
                setOptions(entries);
            } catch (error) {
                if (!cancelled) {
                    dispatch(
                        setAlert({
                            message: getErrorMessage(
                                error,
                                translate('servicesDocuments.loadProvidersFailed')
                            ),
                            type: 'error',
                        })
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [dispatch, enabled, translate]);

    const optionsById = useMemo(() => {
        const map = new Map<number, string>();
        options.forEach((option) => map.set(option.id, option.name));
        return map;
    }, [options]);

    return { options, optionsById, loading };
}
